import { Alert as CustomAlert } from '@/components/ui/alert'; // Assuming your custom alert path
import { useAddBrandPaymentMethodAction } from '@/hooks/actions/use-add-brand-payment-method-action';
import { isStripeConfigured, stripePromise } from '@/libs/stripe-client';
import { Box, Button, VStack } from '@chakra-ui/react';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import type { StripeElementsOptions, StripeError } from '@stripe/stripe-js';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StripeConfigurationAlert } from './StripeConfigurationAlert';

interface StripePaymentFormProps {
  onCancel: () => void;
  onSuccess: (newPaymentMethodId: string) => void; // Called after both Stripe and backend succeed
}

// Options for CardElement appearance and behavior
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
};

const StripeFormContent: React.FC<StripePaymentFormProps> = ({ onCancel, onSuccess }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [stripeError, setStripeError] = useState<StripeError | null>(null);
  const [processingStripe, setProcessingStripe] = useState(false);

  // Use the mutation hook
  const {
    mutate: addPaymentMethod,
    isPending: isSaving,
    error: saveError,
  } = useAddBrandPaymentMethodAction();

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setProcessingStripe(true);
    setStripeError(null);

    if (!stripe || !elements) {
      setStripeError({
        type: 'validation_error',
        message: t('payouts.checkout.paymentMethodModal.stripeForm.stripeNotLoaded'),
      });
      setProcessingStripe(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setStripeError({
        type: 'validation_error',
        message: t('payouts.checkout.paymentMethodModal.stripeForm.cardElementNotFound'),
      });
      setProcessingStripe(false);
      return;
    }

    // 1. Create Payment Method with Stripe
    const { error: createError, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });

    if (createError) {
      console.error('[stripe error]', createError);
      setStripeError(createError);
      setProcessingStripe(false);
      return; // Stop if Stripe fails
    } else if (paymentMethod) {
      // 2. Send Payment Method ID to backend using the mutation
      addPaymentMethod(
        { stripePaymentMethodId: paymentMethod.id },
        {
          onSuccess: (savedPaymentMethod) => {
            // Call the component's onSuccess prop only after backend confirms success
            onSuccess(savedPaymentMethod.id);
            // Reset processing state
            setProcessingStripe(false);
          },
          onError: () => {
            // Error is handled by the hook's onError, but we need to reset processing state here too
            setProcessingStripe(false);
          },
        }
      );
      // Note: isSaving state from the hook will be true now
      // setProcessingStripe(false); // Don't set false here, wait for mutation to finish
    } else {
      setStripeError({
        type: 'api_error',
        message: t('payouts.checkout.paymentMethodModal.stripeForm.createFailed'),
      });
      setProcessingStripe(false);
    }
  };

  const isProcessing = processingStripe || isSaving; // Combined loading state
  const displayError = stripeError || saveError;

  return (
    <VStack as="form" onSubmit={handleFormSubmit} gap={6} align="stretch">
      {/* Display Stripe or Backend errors */}
      {displayError && (
        <CustomAlert
          status="error"
          title={
            displayError instanceof Error
              ? t('payouts.checkout.paymentMethodModal.stripeForm.saveError')
              : displayError.type
          }
        >
          {displayError.message}
        </CustomAlert>
      )}

      <Box p={4} borderWidth={1} rounded="md" bg="gray.50">
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </Box>

      <VStack gap={3} align="stretch">
        <Button
          type="submit"
          disabled={!stripe || isProcessing} // Disable if stripe not loaded or processing/saving
          loading={isProcessing} // Show loading state
          loadingText={t('payouts.checkout.paymentMethodModal.stripeForm.processing')}
        >
          {t('payouts.checkout.paymentMethodModal.stripeForm.addCard')}
        </Button>
        <Button variant="ghost" onClick={onCancel} disabled={isProcessing}>
          {t('payouts.checkout.paymentMethodModal.cancel')}
        </Button>
      </VStack>
    </VStack>
  );
};

// Main component that wraps the form with the Elements provider
export const StripePaymentForm: React.FC<StripePaymentFormProps> = ({ onCancel, onSuccess }) => {
  const { t } = useTranslation();

  if (!isStripeConfigured) {
    return (
      <VStack gap={3} align="stretch">
        <StripeConfigurationAlert />
        <Button variant="ghost" onClick={onCancel}>
          {t('payouts.checkout.paymentMethodModal.cancel')}
        </Button>
      </VStack>
    );
  }

  // Options for the Elements provider
  const options = {
    // To disable Link:
    paymentMethodCreation: 'manual', // Required when customizing payment method creation flow
    // You might need other options like appearance or clientSecret depending on full integration
    // appearance: { theme: 'stripe' },
  } as StripeElementsOptions;

  return (
    <Elements stripe={stripePromise} options={options}>
      <StripeFormContent onCancel={onCancel} onSuccess={onSuccess} />
    </Elements>
  );
};
